// File for logic corresponding to header component
import { Component } from 'js-component-framework';
import { AriaPopup } from 'js-component-framework/lib/plugins';
import Headroom from 'headroom.js';
import Debounce from 'utils/Debounce';
import isMobile from 'utils/isMobile';
import isTablet from 'utils/isTablet';
import './index.scss';

/**
 * Component for site header
 */
export default class Header extends Component {
  /**
   * Remove positioning to dropdown.
   */
  static removeDropDownPosition(event) {
    const dropDownParent = event.currentTarget;
    const dropDown = dropDownParent.querySelector('.dropnav');
    dropDown.style.left = '';
  }

  static buildMoreSubItems(items, resize) {
    let itemsString = '';
    items.forEach((item) => {
      // Get Submenu items
      const links = item.querySelectorAll('.sub-menu a');
      // Get main item
      const main = item.querySelector(':scope > a');
      let subitems = '';

      links.forEach((link) => {
        subitems += `
        <li class="submenu-item__more-button">${link.outerHTML}</li>`;
      });

      if (! resize) {
        itemsString += `
        <ul class="sub-menu menu-item__hidden">
          <li id
          const menuStri="menu-item" class="menu-item">${main.outerHTML}</li>
          ${subitems}
        </ul>`;
      } else {
        itemsString += `
        <ul class="sub-menu sub-menu__columns-${items.length}">
          <li id="menu-item" class="menu-item">${main.outerHTML}</li>
          ${subitems}
        </ul>`;
        item.classList.add('menu-item__hidden');
      }
    });

    const menuString = `
      <li id="more-item" class="menu-item menu-item-has-children">
        <a>More</a>
        <div class="dropnav">
          <div id="more-item__subitems"
          class="dropnav__section dropnav__section__more-button
          more-item__subitems">
            ${itemsString}
          </div>
        </div>
      </li>`;

    return menuString;
  }

  // Find menu items that don't fit in the supernav
  static findItemsToHide(items, targetPosition) {
    const newTargetPosition = targetPosition;
    const hiddenItems = [];
    for (let i = newTargetPosition; i < items.length; i += 1) {
      hiddenItems.push(items[i]);
    }
    return hiddenItems;
  }

  static findTargetPosition(items, targetPosition) {
    const minIndex = Array.from(items).findIndex((item) => 0 < item.offsetTop);
    // eslint-disable-next-line max-len
    const newtargetPosition = minIndex < targetPosition ? minIndex : targetPosition;
    return newtargetPosition;
  }

  static hideSuperNavItems(items, targetPosition, superNav, moreButton) {
    // eslint-disable-next-line max-len
    const moreSubItems = moreButton.querySelector('#more-item__subitems').children;
    let newTargetPosition = targetPosition;
    newTargetPosition -= 1;
    for (let i = newTargetPosition; i < items.length; i += 1) {
      if ('more-item' === items[i].id) {
        items[i].classList.remove('menu-item__hidden');
        // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore
        superNav.insertBefore(moreButton, items[newTargetPosition]);
        break;
      } else {
        moreSubItems.item(i).classList.remove('menu-item__hidden');
        moreSubItems.item(i).classList.add('sub-menu__columns');
        items[i].classList.add('menu-item__hidden');
      }
    }
  }

  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    this.headroom = null;
    this.toggleMenu = this.toggleMenu.bind(this);
    this.children.navToggle.addEventListener('click', this.toggleMenu);

    this.createToggleIndicator = this.createToggleIndicator.bind(this);
    // eslint-disable-next-line max-len, import/no-webpack-loader-syntax, import/no-unresolved, global-require
    this.toggleIcon = require('!svg-inline-loader!./chevron.svg');
    this.toggleIndicator = this.createToggleIndicator();
    // Init More link on component start component
    this.initMoreButton = this.initMoreButton.bind(this);

    this.setDropDownPosition = this.setDropDownPosition.bind(this);
    this.shouldSetDropDownPosition = this.shouldSetDropDownPosition.bind(this);
    this.onResize = this.onResize.bind(this);
    // On Resize, redo the More Link
    this.onResizeDebounce = new Debounce(
      this.onResize,
      'resize',
      window,
      250,
      false,
    );

    // Listen for initSubMenu ready event
    window.addEventListener('navSubMenuReady', this.initMoreButton);

    this.initSubMenu();
    this.initHeadroom();
    this.shouldSetDropDownPosition();
  }

  /**
   * Actions on viewport resize.
   */
  onResize() {
    this.shouldSetDropDownPosition();
    setTimeout(500, this.initMoreButton());
  }

  /**
   * Set dropdown position reltative to site menu.
   *
   * @param {object} event event properties.
   */
  setDropDownPosition(event) {
    const dropDownParent = event.currentTarget;
    const dropDown = dropDownParent.querySelector('.dropnav');
    const { menuItem } = this.children;
    let xPos = null;

    if (! dropDownParent || ! dropDown || ! menuItem) {
      return;
    }

    // Dropdowns w/ articles are full-width.
    if (dropDownParent.classList.contains('menu-item-has-articles')) {
      return;
    }

    // eslint-disable-next-line max-len
    const index = Array.prototype.slice.call(menuItem).indexOf(dropDownParent) + 1;

    // First 5 dropdown are aligned left to parent menu. Remaining are aligned
    // right to parent menu. Avoids dropnav on desktop being cut off by right
    // edge of browser.
    if (5 >= index) {
      xPos = dropDownParent.offsetLeft;
    } else {
      xPos = dropDownParent.offsetLeft
        - (dropDown.offsetWidth - dropDownParent.offsetWidth);
    }
    if (xPos) {
      dropDown.style.left = `${xPos}px`;
    }
  }

  /**
   * Initialize submenu.
   */
  initSubMenu() {
    // eslint-disable-next-line max-len
    Array.prototype.forEach.call(this.children.parentMenuItem, (menuItem, index) => {
      const subMenuTarget = menuItem.querySelector('.dropnav');

      if (null !== subMenuTarget) {
        // eslint-disable-next-line function-paren-newline
        menuItem.insertBefore(
          this.toggleIndicator.cloneNode(true),
          subMenuTarget,
        );

        // eslint-disable-next-line no-unused-vars
        const popup = new AriaPopup({
          controller: menuItem.querySelector('.menu-item__toggle'),
          target: subMenuTarget,
          attributes: {
            label: `${menuItem.querySelector('a').innerText}`,
          },
        });
      }

      if (index >= this.children.parentMenuItem.length - 1) {
        // Dispatch SubMenu ready event
        window.dispatchEvent(
          new CustomEvent('navSubMenuReady'),
        );
      }
    });
  }

  /**
   * Initialize the More button.
   */
  initMoreButton() {
    if (isMobile() || isTablet()) {
      return;
    }

    const { superNav } = this.children;
    try {
      let moreButton = superNav.querySelector('#more-item');

      if (! moreButton) {
        // Create the more-item for the first time and hide it so it doesn't
        // cause flickering on load
        // View: https://nexstardigital.atlassian.net/browse/WC-1027
        // Get all elements from superNav.
        const items = superNav.querySelectorAll(':scope > li');
        // This var is used to get the index where the more buton will be added.
        let targetPosition = 9 < items.length ? 9 : items.length;

        // Create the hidden items in the more button
        const stringMoreLink = Header.buildMoreSubItems(items, false);
        const menuRange = document.createRange();
        const menuFragment = menuRange.createContextualFragment(stringMoreLink);
        // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore
        superNav.insertBefore(menuFragment, items[targetPosition]);
        moreButton = superNav.querySelector('#more-item');
        moreButton.classList.add('menu-item__hidden');

        targetPosition = Header.findTargetPosition(items, targetPosition);
        const hiddenItems = Header.findItemsToHide(items, targetPosition);

        if (1 <= hiddenItems.length && 'undefined' !== typeof hiddenItems[0]) {
          Header.hideSuperNavItems(items, targetPosition, superNav, moreButton);
          moreButton.classList.remove('menu-item__hidden');
        }
        // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore
        superNav.insertBefore(moreButton, items[targetPosition]);
      } else {
        // This part of the code, recreates the menu on resize, is a way
        // different than before, since in the previous funcion we ad adding
        // the More button on load, and moving it into the supernav
        moreButton = superNav.querySelector('#more-item');
        moreButton.remove();

        const items = superNav.querySelectorAll(':scope > li');
        const hiddenItems = [];
        let targetPosition = 9 < items.length ? 9 : items.length;
        if (items) {
          items.forEach((item, index) => {
            // Remove hidden class from all items
            item.classList.remove('menu-item__hidden');
            if (0 < item.offsetTop) {
              // Find position to insert the More element
              targetPosition = index < targetPosition ? index : targetPosition;
            }
          });
        }

        targetPosition -= 1;
        // Find menu items that don't fit in the supernav
        for (let i = targetPosition; i < items.length; i += 1) {
          hiddenItems.push(items[i]);
        }
        if (1 < hiddenItems.length) {
          const stringMoreLink = Header.buildMoreSubItems(hiddenItems, true);
          const menuRange = document.createRange();
          // eslint-disable-next-line max-len
          const menuFragment = menuRange.createContextualFragment(stringMoreLink);
          // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore
          superNav.insertBefore(menuFragment, items[targetPosition]);
        }
      }
    } finally {
      // Show supernav once the More button is ready.
      superNav.style.visibility = 'visible';
    }
  }

  /**
   * Add classes based on scrolling direction and coordinates.
   * Used for sticky header.
   *
   * @ref http://wicky.nillia.ms/headroom.js/
   */
  initHeadroom() {
    let offset = this.options.brandingHeight;

    // Create a default instance of Headroom using a default offset.
    this.createHeadroom(offset);

    // When the Super Billboard ad is enabled, the sticky header offset can not
    // be calcuated until after the ad has been inserted and is completely loaded
    // into place. We use MutationObserver to react to these changes. See LAK-1031
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        // Set the offset based on the vertical position of the header.
        const newOffset = this.element.getBoundingClientRect().top;

        // Assumes that a new offset should only be set if the header vertical
        // position is greater than the default offset. This would indicate that
        // a Super Billboard ad is being loaded above the sticky header.
        if (newOffset > offset) {
          offset = newOffset;

          // Create a new instance of Headroom based on new offset.
          this.createHeadroom(offset);
        }
      });
    });
    // We need to target both the <html> and <body> tag for changes to the style
    // attribute since some active ads will apply styles unpredictably to either
    // tag.
    const observerConfig = {
      attributes: true,
      attributeFilter: ['style'],
    };
    observer.observe(document.documentElement, observerConfig);
    observer.observe(document.body, observerConfig);
  }

  /**
   * Toggle navigation menu.
   */
  toggleMenu() {
    const { nav, navToggle, navContent } = this.children;

    if (! nav || ! navToggle || ! navContent) {
      return;
    }

    if ('false' === navToggle.getAttribute('aria-expanded')) {
      navToggle.setAttribute('aria-expanded', 'true');
      nav.classList.add(this.options.navOpenClass);
    } else {
      navContent.scrollTop = 0; // See NNN-294
      navToggle.setAttribute('aria-expanded', 'false');
      nav.classList.remove(this.options.navOpenClass);
    }
  }

  /**
   * Create toggle icon.
   */
  createToggleIndicator() {
    const tagString = `<button class="menu-item__toggle">
    <span class="icon">
      ${this.toggleIcon}
    </span></button`;
    const range = document.createRange();
    return range.createContextualFragment(tagString);
  }

  /**
   * Create Headroom.
   */
  createHeadroom(offset) {
    this.headroom = new Headroom(this.element, {
      offset,
      tolerance: 0,
      // css classes to apply
      classes: {
        // when element is initialised
        initial: 'is-scrolling',
        // when scrolling down
        pinned: 'is-scrolling--down',
        // when scrolling up
        unpinned: 'is-scrolling--up',
        // when above offset
        top: 'is-scrolling--top',
        // when below offset
        notTop: 'is-scrolling--not-top',
        // when at bottom of scoll area
        bottom: 'is-scrolling--bottom',
        // when not at bottom of scroll area
        notBottom: 'is-scrolling--not-bottom',
      },
    });
    this.headroom.init();
  }

  /**
   * Determine if dropdown position should be set and add/remove event
   * listeners accordingly.
   */
  shouldSetDropDownPosition() {
    if (matchMedia('(min-width: 1025px)').matches) {
      Array.prototype.forEach.call(this.children.parentMenuItem, (menuItem) => {
        menuItem.addEventListener('mouseenter', this.setDropDownPosition);
        menuItem.addEventListener('mouseleave', Header.removeDropDownPosition);
      });
    } else if (matchMedia('(max-width: 1024px)').matches) {
      Array.prototype.forEach.call(this.children.parentMenuItem, (menuItem) => {
        menuItem.removeEventListener('mouseenter', this.setDropDownPosition);
        menuItem.removeEventListener(
          'mouseleave',
          Header.removeDropDownPosition,
        );
      });
    }
  }
}
